import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatApiError, todayISO } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calculator, Receipt, AlertCircle, ArrowRight, Loader2, FileSpreadsheet, FileText, ListChecks, CheckCircle2, Mail } from "lucide-react";
import PayrollExportTab from "@/components/PayrollExportTab";
import { AdminPaystubs } from "@/pages/admin/AdminPaystubsPage";

export default function AdminPayrollHub() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "review";
  return (
    <div className="space-y-5" data-testid="admin-payroll-hub">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Payroll</h1>
        <p className="text-sm text-slate-500 mt-1">
          Review weekly pay, generate signed PDF paystubs, and export payroll-ready CSVs for QuickBooks, Gusto, ADP, and other payroll software.
        </p>
      </header>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList className="bg-slate-100" data-testid="payroll-tabs">
          <TabsTrigger value="review" data-testid="tab-review"><ListChecks className="h-4 w-4 mr-1.5" /> Review</TabsTrigger>
          <TabsTrigger value="paystubs" data-testid="tab-paystubs"><FileText className="h-4 w-4 mr-1.5" /> PDF Paystubs</TabsTrigger>
          <TabsTrigger value="export" data-testid="tab-export"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> CSV Export</TabsTrigger>
          <TabsTrigger value="late" data-testid="tab-late"><AlertCircle className="h-4 w-4 mr-1.5" /> Late Submissions</TabsTrigger>
        </TabsList>
        <TabsContent value="review" className="mt-4"><PayrollReviewTab /></TabsContent>
        <TabsContent value="paystubs" className="mt-4"><AdminPaystubs /></TabsContent>
        <TabsContent value="export" className="mt-4"><PayrollExportTab /></TabsContent>
        <TabsContent value="late" className="mt-4"><LateSubmissionsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function PayrollReviewTab() {
  const [weekStart, setWeekStart] = useState(todayISO());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [repairing, setRepairing] = useState(false);
  // Close-week confirm dialog state. `closePreview` is {rows:[{employee_id,
  // employee_name, email, missing_dates:[]}]} returned by the backend
  // preview endpoint, loaded when the admin clicks Generate so they see
  // the list of techs still mid-finalize before closing on top of them.
  const [closeOpen, setCloseOpen] = useState(false);
  const [closePreview, setClosePreview] = useState(null);
  const [closeLoading, setCloseLoading] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [inspectResult, setInspectResult] = useState(null);
  const navigate = useNavigate();

  const inspectMissingDay = async (employeeId, date) => {
    try {
      const { data } = await api.get("/payroll/missing-day-inspect", {
        params: { employee_id: employeeId, date },
      });
      setInspectResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const loadPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/preview", { week_start: weekStart });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Auto-load preview whenever the week changes
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/payroll/preview", { week_start: weekStart });
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        if (!cancelled) setBusy(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [weekStart]);

  const repairDates = async () => {
    if (!preview) return;
    setRepairing(true);
    try {
      const emp_ids = (preview.missing_days || []).map((m) => m.employee_id);
      const { data } = await api.post("/payroll/repair-dates", {
        week_start: weekStart,
        employee_ids: emp_ids.length ? emp_ids : undefined,
      });
      const fixed = data.fixed || 0;
      const reassigned = data.reassigned || 0;
      if (fixed || reassigned) {
        const parts = [];
        if (fixed) parts.push(`${fixed} date row${fixed === 1 ? "" : "s"} normalized`);
        if (reassigned) parts.push(`${reassigned} orphan submission${reassigned === 1 ? "" : "s"} re-assigned to the active account`);
        toast.success(`${parts.join(" · ")}. Re-loading preview…`);
      } else {
        toast.info("No rows needed repair. Re-loading preview…");
      }
      await loadPreview();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRepairing(false);
    }
  };

  // Open the Close-week confirmation dialog. Loads the preview of techs
  // with unfinalized days in the period FIRST so the admin sees exactly
  // who they're closing on top of before they commit.
  const openCloseDialog = async () => {
    if (!preview || !preview.rows.length) {
      toast.error("No employees with submitted days in this week. Employees must finalize their day first.");
      return;
    }
    setCloseOpen(true);
    setCloseLoading(true);
    setClosePreview(null);
    try {
      const { data } = await api.get("/payroll/close-week/preview", { params: { week_start: weekStart } });
      setClosePreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setClosePreview({ rows: [] });
    } finally { setCloseLoading(false); }
  };

  // Email every listed tech a per-tech digest of their unfinalized days.
  const notifyUnfinalized = async () => {
    if (!closePreview || (closePreview.rows || []).length === 0) return;
    setNotifying(true);
    try {
      const { data } = await api.post("/payroll/close-week/notify", {
        week_start: weekStart,
        employee_ids: closePreview.rows.map((r) => r.employee_id),
      });
      const s = data.sent || 0, f = data.failed || 0;
      if (s && !f) toast.success(`Nudge sent to ${s} tech${s === 1 ? "" : "s"}.`);
      else if (s && f) toast.success(`Nudge sent to ${s}, ${f} failed.`);
      else toast.error("No nudges delivered. Check tech email addresses.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setNotifying(false); }
  };

  // Confirm step — called from the dialog AFTER the admin has seen the
  // unfinalized list. Posts /payroll/generate the same way the old flow
  // did; the dialog closes before the request fires so the UI returns
  // to the normal "Generating…" button state.
  const confirmGenerate = async () => {
    setCloseOpen(false);
    setGenerating(true);
    try {
      const { data } = await api.post("/payroll/generate", { week_start: weekStart });
      const count = data.paystub_ids.length;
      if (count === 0) {
        toast.error("No paystubs generated — no employees have finalized a day this week.");
      } else {
        toast.success(`Generated ${count} paystub${count === 1 ? "" : "s"}`);
        const { data: p } = await api.post("/payroll/preview", { week_start: weekStart });
        setPreview(p);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  const downloadCsv = async () => {
    const resp = await api.get("/reports/payroll.csv", { params: { week_start: weekStart }, responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll_${weekStart}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const rowCount = preview?.rows?.length || 0;
  const totalGross = (preview?.rows || []).reduce((s, r) => s + (r.calc?.gross_pay || 0), 0);

  return (
    <div className="space-y-5" data-testid="admin-payroll-page">

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Week (any day in the week)</Label>
            <Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="w-48" data-testid="payroll-week-input" />
            {preview && (
              <p className="text-xs text-slate-500 mt-1">{fmtDate(preview.week_start)} → {fmtDate(preview.week_end)}</p>
            )}
          </div>
          <Button
            onClick={openCloseDialog}
            disabled={busy || generating || rowCount === 0}
            className="bg-sky-600 hover:bg-sky-700 h-12 px-6 text-base font-semibold"
            data-testid="payroll-generate-btn"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
            ) : (
              <><Receipt className="h-4 w-4 mr-2" /> {rowCount > 0 ? `Generate ${rowCount} Paystub${rowCount === 1 ? "" : "s"}` : "Generate Paystubs"}</>
            )}
          </Button>
          <Button onClick={downloadCsv} disabled={busy || rowCount === 0} variant="outline" className="h-12" data-testid="payroll-csv-btn">
            Export CSV
          </Button>
          <Button variant="ghost" className="h-12 ml-auto text-sky-700" onClick={() => navigate("/admin/paystubs")} data-testid="goto-paystubs-btn">
            View paystubs <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </CardContent>
      </Card>

      {busy && !preview && (
        <Card className="rounded-2xl border-slate-200"><CardContent className="p-8 text-center text-slate-500">Loading preview…</CardContent></Card>
      )}

      {preview && (preview.missing_days || []).length > 0 && (
        <Card className="rounded-2xl border-amber-300 bg-amber-50" data-testid="missing-days-banner">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-amber-900">
                  {preview.missing_days.length} employee{preview.missing_days.length === 1 ? "" : "s"} with activity that didn't make it into this week's paystub
                </h3>
                <p className="text-sm text-amber-800 mt-1">
                  A date is flagged only when it has a <strong>time entry</strong> or a <strong>daily submission</strong> that didn't reach the paystub. Lunch and job entries alone no longer trigger this banner. Click <strong>Re-check & repair</strong> to normalize dates and re-assign orphan user_ids, or <strong>Inspect</strong> any row to see the exact evidence behind it.
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-amber-900">
                  {preview.missing_days.map((m) => (
                    <li key={m.employee_id} data-testid={`missing-day-row-${m.employee_id}`}>
                      <strong>{m.employee_name}</strong>
                      {m.missed && m.missed.length > 0 && (
                        <>
                          : missed {m.missed.map((d) => {
                            const srcs = (m.activity_sources && m.activity_sources[d]) || [];
                            const label = srcs.length ? ` (${srcs.join(", ")})` : "";
                            return (
                              <span key={d}>
                                <button
                                  type="button"
                                  onClick={() => inspectMissingDay(m.employee_id, d)}
                                  className="underline decoration-dotted hover:text-amber-950"
                                  data-testid={`inspect-missing-day-${m.employee_id}-${d}`}
                                >
                                  {fmtDate(d)}{label}
                                </button>
                                {d === m.missed[m.missed.length - 1] ? "" : ", "}
                              </span>
                            );
                          })}
                        </>
                      )}
                      {m.orphan_rows && m.orphan_rows.length > 0 && (
                        <>
                          {" "}· orphan submissions on {m.orphan_rows.map((o) => fmtDate(o.date)).join(", ")}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={repairDates}
                  disabled={repairing || busy}
                  className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="repair-dates-btn"
                >
                  {repairing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Re-checking…</>) : "Re-check & repair"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {inspectResult && (
        <Dialog open={!!inspectResult} onOpenChange={(o) => !o && setInspectResult(null)}>
          <DialogContent className="max-w-2xl" data-testid="inspect-dialog">
            <DialogHeader>
              <DialogTitle>
                {inspectResult.employee_name} · {fmtDate(inspectResult.date)}
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-600 space-y-3 max-h-[60vh] overflow-y-auto">
              <p>
                Raw rows across the four daily collections for this employee and date, scoped to{" "}
                {inspectResult.aliases.length} alias user_id{inspectResult.aliases.length === 1 ? "" : "s"}.
                Rows in <strong>daily_submissions</strong> or <strong>time_entries</strong> can produce
                a paystub row. Rows in <strong>lunch_entries</strong> or <strong>job_entries</strong>
                are attachments only.
              </p>
              {Object.entries(inspectResult.sources || {}).map(([coll, rows]) => (
                <div key={coll} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-xs font-mono text-slate-700">{coll}</code>
                    <span className="text-xs text-slate-500">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
                  </div>
                  {rows.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">no rows</p>
                  ) : (
                    <pre className="text-xs bg-slate-50 p-2 rounded overflow-x-auto">{JSON.stringify(rows, null, 2)}</pre>
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInspectResult(null)} data-testid="inspect-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {preview && rowCount === 0 && (
        <Card className="rounded-2xl border-amber-200 bg-amber-50">
          <CardContent className="p-5 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-900">No employees ready for payroll this week</h3>
              <p className="text-sm text-amber-800 mt-1">
                Paystubs can only be generated from <strong>finalized</strong> daily submissions. Make sure your team has clocked in/out and submitted their days for the week of {fmtDate(preview.week_start)} – {fmtDate(preview.week_end)}.
              </p>
              <Button variant="link" className="text-amber-900 px-0 mt-1" onClick={() => navigate("/admin/submissions")} data-testid="view-submissions-link">
                View daily submissions →
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {preview && rowCount > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Employees" value={rowCount} />
            <Stat label="Total Gross" value={fmtMoney(totalGross)} accent />
            <Stat label="Week Start" value={fmtDate(preview.week_start)} />
            <Stat label="Week End" value={fmtDate(preview.week_end)} />
          </div>
          <Card className="rounded-2xl border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Employee</th>
                    <th className="text-left px-4 py-3 font-semibold">Type</th>
                    <th className="text-right px-4 py-3 font-semibold">Days</th>
                    <th className="text-right px-4 py-3 font-semibold">Reg / OT Hrs</th>
                    <th className="text-right px-4 py-3 font-semibold">Hourly Total</th>
                    <th className="text-right px-4 py-3 font-semibold">Commission</th>
                    <th className="text-left px-4 py-3 font-semibold">Method</th>
                    <th className="text-right px-4 py-3 font-semibold">Gross Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.employee_id} className="border-b border-slate-100" data-testid={`payroll-row-${r.employee_id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{r.employee_name}</td>
                      <td className="px-4 py-3 uppercase text-xs">{r.worker_type}</td>
                      <td className="px-4 py-3 text-right">{r.daily_count}</td>
                      <td className="px-4 py-3 text-right">{r.calc.regular_hours.toFixed(1)} / {r.calc.overtime_hours.toFixed(1)}</td>
                      <td className="px-4 py-3 text-right">{fmtMoney(r.calc.hourly_total)}</td>
                      <td className="px-4 py-3 text-right">{fmtMoney(r.calc.commission_pay)}</td>
                      <td className="px-4 py-3 text-xs">{r.calc.pay_method_used}</td>
                      <td className="px-4 py-3 text-right text-lg font-extrabold text-green-700">{fmtMoney(r.calc.gross_pay)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Close & lock week confirmation. Lists any techs who still have
          unfinalized days in the period so the admin never silently
          closes on top of pending work. Shows a green all-clear when
          every tech is finalized. */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-lg" data-testid="close-week-dialog">
          <DialogHeader>
            <DialogTitle>Close &amp; lock week</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{fmtDate(preview?.week_start)} → {fmtDate(preview?.week_end)}</span>
              {" "}· {rowCount} paystub{rowCount === 1 ? "" : "s"} will be generated. Existing paystubs for this week will be replaced.
            </p>
            {closeLoading && (
              <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="close-week-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking for unfinalized days…
              </div>
            )}
            {!closeLoading && closePreview && (closePreview.rows || []).length === 0 && (
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900" data-testid="close-week-all-clear">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">All techs are finalized.</p>
                  <p className="text-emerald-800">You're clear to close this week.</p>
                </div>
              </div>
            )}
            {!closeLoading && closePreview && (closePreview.rows || []).length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="close-week-blockers">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-900 text-sm">
                      {closePreview.rows.length} tech{closePreview.rows.length === 1 ? "" : "s"} still {closePreview.rows.length === 1 ? "has" : "have"} unfinalized days in this period:
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-amber-900">
                      {closePreview.rows.map((r) => (
                        <li key={r.employee_id} data-testid={`close-week-blocker-${r.employee_id}`}>
                          <span className="font-semibold">{r.employee_name || r.email || "Unknown"}</span>
                          <span className="text-amber-800"> — {(r.missing_dates || []).map((d) => fmtDate(d)).join(", ")}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-amber-700 mt-2">
                      Closing now will auto-lock these days with their current time. Techs can't finalize once locked without admin reopening the period.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setCloseOpen(false)} data-testid="close-week-cancel">
              Cancel
            </Button>
            {!closeLoading && closePreview && (closePreview.rows || []).length > 0 && (
              <Button
                onClick={notifyUnfinalized}
                disabled={notifying}
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="close-week-notify-btn"
              >
                {notifying ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : (<><Mail className="h-4 w-4 mr-2" /> Notify these techs</>)}
              </Button>
            )}
            <Button
              onClick={confirmGenerate}
              disabled={closeLoading}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="close-week-confirm"
            >
              {closePreview && (closePreview.rows || []).length > 0 ? "Close anyway" : "Close & lock week"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <Card className="rounded-2xl border-slate-200">
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
        <p className={`text-2xl font-extrabold font-display mt-1 ${accent ? "text-green-700" : "text-slate-900"}`}>{value}</p>
      </CardContent>
    </Card>
  );
}


/* ========================================================================
 * Late Submissions tab (Phase 2 of the pre-close back-fix plan).
 *
 * Admin reviews tech requests that fell outside the normal 8-day back-
 * finalize window — either because more than 8 calendar days have passed,
 * or because the pay period for that day has already been admin-approved.
 * The "Approve & apply" button fires the backend orchestrator which:
 *   1. reopens the pay period
 *   2. inserts the daily_submission from the request's stored snapshot
 *   3. re-generates paystubs for that week (upsert)
 *   4. relocks the pay period
 *   5. marks the request approved
 * ========================================================================
 */
function LateSubmissionsTab() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [rejectReqId, setRejectReqId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/late-submissions", { params: { status } });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (reqId) => {
    if (!confirm("Approve & apply? This will reopen the pay period, add the day to the paystub, regenerate the week, and relock the period. Proceed?")) return;
    setActingId(reqId);
    try {
      const { data } = await api.post(`/admin/late-submissions/${reqId}/approve`, { decision_notes: null });
      toast.success(`Applied. Submission ${data.submission_id?.slice(0, 8)} folded into the week.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActingId(null); }
  };

  const confirmReject = async () => {
    if (!rejectReqId) return;
    if ((rejectNotes || "").trim().length < 5) {
      toast.error("Please add a note (at least 5 characters) explaining the rejection.");
      return;
    }
    setActingId(rejectReqId);
    try {
      await api.post(`/admin/late-submissions/${rejectReqId}/reject`, { decision_notes: rejectNotes.trim() });
      toast.success("Request rejected.");
      setRejectReqId(null); setRejectNotes("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActingId(null); }
  };

  return (
    <Card className="rounded-2xl border-slate-200" data-testid="late-submissions-tab">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="text-lg font-extrabold text-slate-900">Late finalize requests</h2>
          <div className="flex items-center gap-2">
            {["pending", "approved", "rejected", "all"].map((s) => (
              <Button
                key={s}
                variant={status === s ? "default" : "outline"}
                size="sm"
                onClick={() => setStatus(s)}
                className={status === s ? "bg-sky-600 hover:bg-sky-700 h-9 capitalize" : "h-9 capitalize"}
                data-testid={`late-filter-${s}`}
              >
                {s}
              </Button>
            ))}
          </div>
        </div>

        {busy && (
          <div className="py-8 text-center text-slate-500 text-sm"><Loader2 className="h-4 w-4 inline mr-2 animate-spin" /> Loading…</div>
        )}
        {!busy && rows.length === 0 && (
          <div className="py-10 text-center text-slate-500 text-sm" data-testid="late-empty">
            No {status === "all" ? "" : status} late-finalize requests.
          </div>
        )}
        {!busy && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="late-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2.5">Technician</th>
                  <th className="px-3 py-2.5">Day</th>
                  <th className="px-3 py-2.5">Reason</th>
                  <th className="px-3 py-2.5">Why late</th>
                  <th className="px-3 py-2.5 text-right">Hours</th>
                  <th className="px-3 py-2.5 text-right">Est. pay</th>
                  <th className="px-3 py-2.5">Submitted</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`late-row-${r.id}`}>
                    <td className="px-3 py-3 font-semibold text-slate-900">{r.employee_name || r.employee_email || "—"}</td>
                    <td className="px-3 py-3">{fmtDate(r.date)}</td>
                    <td className="px-3 py-3 text-slate-600 max-w-[280px]"><span className="line-clamp-2">{r.reason}</span></td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center text-[11px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded ${r.blocker === "locked_period" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                        {r.blocker === "locked_period" ? "Period locked" : `${r.days_back}d back`}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">{(r.snapshot?.hours?.net_hours || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-right">{fmtMoney(r.snapshot?.pay?.estimated_final_pay || 0)}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}</td>
                    <td className="px-3 py-3 text-right">
                      {r.status === "pending" ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            disabled={actingId === r.id}
                            onClick={() => approve(r.id)}
                            className="bg-emerald-600 hover:bg-emerald-700 h-9"
                            data-testid={`late-approve-${r.id}`}
                          >
                            {actingId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Approve &amp; apply</>}
                          </Button>
                          <Button
                            size="sm"
                            disabled={actingId === r.id}
                            variant="outline"
                            onClick={() => { setRejectReqId(r.id); setRejectNotes(""); }}
                            className="h-9"
                            data-testid={`late-reject-${r.id}`}
                          >
                            Reject
                          </Button>
                        </div>
                      ) : (
                        <span className={`text-[11px] font-extrabold uppercase tracking-wider ${r.status === "approved" ? "text-emerald-700" : "text-slate-500"}`}>
                          {r.status}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Reject-with-notes modal */}
      <Dialog open={!!rejectReqId} onOpenChange={(open) => { if (!open) { setRejectReqId(null); setRejectNotes(""); } }}>
        <DialogContent className="max-w-md" data-testid="late-reject-dialog">
          <DialogHeader><DialogTitle>Reject this request?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-600">
              Add a short note so the tech knows why. The paystub for that week is left untouched.
            </p>
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Reason for rejection (5-500 chars)…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="late-reject-notes"
            />
            <p className="text-xs text-slate-500">{rejectNotes.length}/500</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setRejectReqId(null); setRejectNotes(""); }} data-testid="late-reject-cancel">Cancel</Button>
            <Button
              onClick={confirmReject}
              disabled={actingId === rejectReqId}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="late-reject-confirm"
            >
              {actingId === rejectReqId ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reject request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
